<template>
  <a-modal
    v-model:visible="visibility"
    title="Editar datos colaborador"
    @cancel="closeModal"
    :closable="!loading"
    :maskClosable="!loading"
  >
    <a-form
      ref="formRef"
      layout="vertical"
      :model="formState"
      :rules="rules"
      name="edit-employee-info"
    >
      <a-form-item label="RUT">
        <a-input :value="rutToolBox.formatRut(employeeData?.rut)" disabled />
      </a-form-item>

      <a-form-item name="firstName" label="Primer nombre" required>
        <a-input
          v-model:value="formState.firstName"
          placeholder="Ingrese primer nombre"
          :maxlength="40"
        />
      </a-form-item>

      <a-form-item name="secondName" label="Segundo nombre">
        <a-input
          v-model:value="formState.secondName"
          placeholder="Ingrese segundo nombre"
          :maxlength="40"
        />
      </a-form-item>

      <a-form-item name="firstSurname" label="Primer apellido" required>
        <a-input
          v-model:value="formState.firstSurname"
          placeholder="Ingrese primer apellido"
          :maxlength="40"
        />
      </a-form-item>

      <a-form-item name="secondSurname" label="Segundo apellido">
        <a-input
          v-model:value="formState.secondSurname"
          placeholder="Ingrese segundo apellido"
          :maxlength="40"
        />
      </a-form-item>

      <a-form-item name="birthdayDate" label="Fecha de nacimiento">
        <a-date-picker
          v-model:value="formState.birthdayDate"
          format="DD-MM-YYYY"
          placeholder="Seleccione fecha de nacimiento"
          :disabled-date="isFutureDate"
          :style="{ width: '100%' }"
        />
        <a-alert
          v-if="isUnderPolicyHolderMinAge(formState.birthdayDate)"
          type="warning"
          show-icon
          :message="POLICY_HOLDER_UNDERAGE_WARNING"
          class="mt-2"
        />
      </a-form-item>

      <a-form-item name="email" label="Correo" required>
        <a-input
          v-model:value="formState.email"
          placeholder="Ingrese correo electrónico"
          :maxlength="120"
        />
      </a-form-item>
    </a-form>

    <template #footer>
      <a-button @click="closeModal" :disabled="loading">Cancelar</a-button>
      <a-button
        type="primary"
        @click="saveChanges"
        :loading="loading"
        :disabled="saveButtonDisabled"
      >
        Guardar cambios
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { computed, defineEmits, defineExpose, reactive, ref } from 'vue';
import { Modal } from 'ant-design-vue';
import moment from 'moment';
import { patchDataEmployee } from '@/network';
import notification from '@/utils/notifications';
import emailValidator from '@/utils/emailValidator';
import rutToolBox from '@/utils/Ruts';
import {
  isFutureDate,
  isUnderPolicyHolderMinAge,
  POLICY_HOLDER_UNDERAGE_WARNING
} from '@/utils/policyHolderBirthday';

const emit = defineEmits(['saved']);

const visibility = ref(false);
const loading = ref(false);
const formRef = ref();
const employeeData = ref(null);
const originalPayload = ref(null);

const formState = reactive({
  firstName: '',
  secondName: '',
  firstSurname: '',
  secondSurname: '',
  email: '',
  birthdayDate: null
});

const requiredTextFieldValidator = (label) => async (_, value) => {
  if (!value?.trim()) {
    return Promise.reject(`Ingrese ${label}`);
  }

  return Promise.resolve();
};

const emailFieldValidator = async (_, value) => {
  if (!value) {
    return Promise.reject('Ingrese correo electrónico');
  }

  if (!emailValidator(value)) {
    return Promise.reject('Correo inválido');
  }

  return Promise.resolve();
};

const rules = {
  firstName: [
    {
      required: true,
      validator: requiredTextFieldValidator('primer nombre'),
      trigger: 'change'
    }
  ],
  firstSurname: [
    {
      required: true,
      validator: requiredTextFieldValidator('primer apellido'),
      trigger: 'change'
    }
  ],
  email: [{ required: true, validator: emailFieldValidator, trigger: 'change' }]
};

const normalizeNameText = (value) =>
  value
    ?.trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase()) || '';

const normalizedForm = computed(() => ({
  firstName: normalizeNameText(formState.firstName),
  secondName: normalizeNameText(formState.secondName),
  firstSurname: normalizeNameText(formState.firstSurname),
  secondSurname: normalizeNameText(formState.secondSurname),
  email: formState.email.trim(),
  birthdayDate: formState.birthdayDate
    ? moment(formState.birthdayDate).format('YYYY-MM-DD')
    : null
}));

const saveButtonDisabled = computed(
  () =>
    JSON.stringify(normalizedForm.value) ===
    JSON.stringify(originalPayload.value)
);

const buildEmployeePayload = ({
  firstName,
  secondName,
  firstSurname,
  secondSurname
}) => {
  const normalizedFirstName = normalizeNameText(firstName);
  const normalizedFirstSurname = normalizeNameText(firstSurname);
  const normalizedSecondName = normalizeNameText(secondName) || null;
  const normalizedSecondSurname = normalizeNameText(secondSurname) || null;

  return {
    name: normalizedFirstName,
    surname: normalizedFirstSurname,
    firstName: normalizedFirstName,
    firstSurname: normalizedFirstSurname,
    secondName: normalizedSecondName,
    secondSurname: normalizedSecondSurname
  };
};

const openModal = (employee) => {
  employeeData.value = employee;
  formState.firstName = employee?.firstName || employee?.name || '';
  formState.secondName = employee?.secondName || '';
  formState.firstSurname = employee?.firstSurname || employee?.surname || '';
  formState.secondSurname = employee?.secondSurname || '';
  formState.email = employee?.email || '';
  formState.birthdayDate =
    employee?.birthdayDate && moment(employee.birthdayDate, 'YYYY-MM-DD');
  originalPayload.value = { ...normalizedForm.value };
  visibility.value = true;
};

const closeModal = () => {
  visibility.value = false;
  formState.birthdayDate = null;
  formRef.value?.resetFields();
};

const persistChanges = async () => {
  try {
    loading.value = true;
    const values = await formRef.value.validate();
    const payload = {};

    const currentNormalizedValues = {
      firstName: normalizeNameText(values.firstName),
      secondName: normalizeNameText(values.secondName),
      firstSurname: normalizeNameText(values.firstSurname),
      secondSurname: normalizeNameText(values.secondSurname),
      email: values.email.trim(),
      birthdayDate: values.birthdayDate
        ? moment(values.birthdayDate).format('YYYY-MM-DD')
        : null
    };

    if (
      currentNormalizedValues.firstName !== originalPayload.value?.firstName ||
      currentNormalizedValues.secondName !==
        originalPayload.value?.secondName ||
      currentNormalizedValues.firstSurname !==
        originalPayload.value?.firstSurname ||
      currentNormalizedValues.secondSurname !==
        originalPayload.value?.secondSurname ||
      currentNormalizedValues.birthdayDate !==
        originalPayload.value?.birthdayDate
    ) {
      Object.assign(payload, buildEmployeePayload(currentNormalizedValues));
      if (currentNormalizedValues.birthdayDate !== originalPayload.value?.birthdayDate) {
        payload.birthdayDate = currentNormalizedValues.birthdayDate;
      }
    }

    if (currentNormalizedValues.email !== originalPayload.value?.email) {
      payload.email = currentNormalizedValues.email;
    }

    await patchDataEmployee(employeeData.value.id, payload);

    notification('success', 'Correcto', 'Datos del colaborador actualizados');
    emit('saved');
    closeModal();
  } catch (error) {
    if (error?.errorFields) {
      return;
    }

    notification(
      'error',
      'Error',
      error?.response?.data?.message ||
        'No fue posible actualizar al colaborador'
    );
  } finally {
    loading.value = false;
  }
};

const saveChanges = async () => {
  try {
    await formRef.value.validate();

    Modal.confirm({
      title: 'Confirmar cambios',
      content:
        'Se guardarán los cambios realizados en los datos del colaborador. Si realiza estos cambios, se debe informar a la compañía. ¿Desea continuar?',
      okText: 'Guardar cambios',
      cancelText: 'Cancelar',
      onOk: () => persistChanges()
    });
  } catch (error) {
    if (error?.errorFields) {
      return;
    }

    notification(
      'error',
      'Error',
      error?.response?.data?.message || 'No fue posible validar el formulario'
    );
  }
};

defineExpose({ openModal });
</script>
